<script setup lang="ts">
import { computed, unref } from 'vue';
import consola from 'consola';
import { useInstitutionStore } from '@entities/institution';
import type { CartItem } from '@entities/order';
import { enrichCartItems, useGiftTiers, useWeeklyGift } from '@features/basket';

const props = defineProps<{ items: CartItem[] }>();

const institutionStore = useInstitutionStore();
const giftTiers = useGiftTiers();
const weeklyGift = useWeeklyGift();

const validItems = computed(() => {
  const items = unref(props.items) || [];

  if (!Array.isArray(items)) {
    consola.warn('[BasketProductList] Items is not an array:', items);
    return [];
  }

  return items.filter((item: any) => {
    if (!item) {
      consola.warn('[BasketProductList] Skipping invalid item:', item);
      return false;
    }
    return true;
  });
});

const enrichedItemsMap = computed(() => {
  const items = validItems.value;

  const productItems = items.filter((item: any) => {
    if (item.itemType === 'combo') return false;
    if (item.combo_id && !item.itemType) return false;
    return true;
  });

  const placeholderImg = (institutionStore.institution as any)?.visual?.placeholder_file;

  const allProducts: any[] = [];
  if (institutionStore.products && Array.isArray(institutionStore.products)) {
    institutionStore.products.forEach((category: any) => {
      if (category.products && Array.isArray(category.products)) {
        allProducts.push(...category.products);
      }
    });
  }

  const enriched = enrichCartItems(
    productItems,
    institutionStore.addons as any,
    institutionStore.excludeIngredients || [],
    placeholderImg,
    allProducts,
  );

  // Подарок за сумму отмечаем по механике, а не по флагу на позиции: корзина
  // авторизованного приезжает с сервера, который isGift/giftSource не хранит, и после
  // перезагрузки пометка терялась — строка рисовалась обычной платной, хотя скидка за
  // подарок применена. Тогда строки переставали сходиться с «Суммой заказа».
  const giftProductId = giftTiers.grantedGiftProductId.value;
  // Подарок за частоту — своя механика со своим владением, но проблема та же.
  const weeklyGiftProductId = weeklyGift.isGiftInCart.value
    ? weeklyGift.progress.value.giftProductId
    : null;

  const map = new Map();
  enriched.forEach(item => {
    if (giftProductId && String(item.id) === giftProductId) {
      item.isGift = true;
    }
    if (weeklyGiftProductId && String(item.id) === weeklyGiftProductId) {
      item.isGift = true;
    }
    const key = getItemKey(item);
    map.set(key, item);
  });

  return map;
});

const getItemKey = (item: any): string => {
  if (!item) return `undefined-${Math.random()}`;
  if (item.itemType === 'combo') return `combo_${item.id}_${item.branch_id}`;
  return `${item.id}_${item.branch_id}_${JSON.stringify(item.addons)}`;
};

const getEnrichedItem = (item: CartItem): any => {
  const key = getItemKey(item);
  return enrichedItemsMap.value.get(key) || item;
};
</script>

<template>
  <section class="list-product">
    <template v-for="item in validItems" :key="getItemKey(item)">
      <!-- Комбо товар (новый формат) -->
      <BasketComboCard
        v-if="(item as any).itemType === 'combo'"
        :item="item as any"
      />

      <!-- Старое комбо (legacy формат) -->
      <div
        v-else-if="(item as any).combo_id && !(item as any).itemType"
        class="legacy-combo-warning"
      >
        <p>⚠️ Устаревший формат комбо: {{ (item as any).name }}</p>
        <p>Пожалуйста, удалите и добавьте заново</p>
      </div>

      <!-- Обычный товар -->
      <BasketProductCard
        v-else
        :item="getEnrichedItem(item)"
      />
    </template>
  </section>
</template>

<style lang="scss" scoped>
.list-product {
  @apply grid gap-3 mb-10 box-border;
}

.legacy-combo-warning {
  padding: 16px;
  background: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: 8px;
  color: #856404;
  text-align: center;

  p {
    margin: 4px 0;
    font-size: 14px;

    &:first-child {
      font-weight: 600;
    }
  }
}
</style>
